import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";

// Material MDC Imports
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatChipsModule } from "@angular/material/chips";

import { MatTooltipModule } from "@angular/material/tooltip";
import { MatMenuModule } from "@angular/material/menu";
import { GridSearchResultsComponent } from "../search-results/grid-result/grid-search-results.component";
import { SearchSummaryPanelComponent } from "../search-summary-panel";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IGeofencingSearchValidation, SearchState, SearchPanelConfig } from "../../model";
import { GeofencingSearchFilterService, SearchQueryService } from "../../services/search";
import { Observable } from "rxjs";

/**
 * Grid display component for search results
 * Displays search results in a responsive grid layout with Material Design
 */
@Component({
  selector: "app-compound-grid-display",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./grid-display-search.component.html",
  styleUrls: ["./grid-display-search.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    MatMenuModule,
    GridSearchResultsComponent,
    SearchSummaryPanelComponent,
  ],
})
export class GridDisplaySearchComponent implements OnInit {
  @ViewChild("container", { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  containerSize: { width: number; height: number } = { width: 0, height: 0 };

  searchQueryService = inject(SearchQueryService);

  private geofencingFilterService = inject(GeofencingSearchFilterService);

  private destroyRef = inject(DestroyRef);

  private cdr = inject(ChangeDetectorRef);

  /** Current search state */
  searchState: SearchState | null = null;

  /**
   * Geofencing precondition of the embeddings search — example searches cannot run until a
   * valid geofencing area is configured.
   */
  geofencingValidation$: Observable<IGeofencingSearchValidation> = this.searchQueryService.geofencingValidation$;

  /** Configuration for the search panel */
  searchPanelConfig: SearchPanelConfig = {
    showExportButton: true,
    showSaveButton: true,
    showClearButton: true,
    customClasses: ["grid-search-panel"],
  };

  ngOnInit(): void {
    this.subscribeToSearchState();
    this.subscribeSearchDisplayMode();
  }

  private subscribeSearchDisplayMode(): void {
    this.searchQueryService.searchDisplayMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateContainerSize();
    });
  }

  updateContainerSize(): void {
    if (this.containerRef) {
      const rect = this.containerRef.nativeElement.getBoundingClientRect();
      this.containerSize = { width: rect.width, height: rect.height };
      this.cdr.markForCheck();
    }
  }

  /**
   * Subscribes to search state changes from the service
   */
  private subscribeToSearchState(): void {
    this.searchQueryService.searchState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.searchState = state;
      this.cdr.detectChanges();
    });
  }

  /**
   * Performs a search with one of the suggested queries
   * @param query The suggestion query to search for
   */
  performSuggestionSearch(query: string): void {
    this.searchQueryService.performTextSearch(query, true).subscribe((results) => {
      console.log("handle results");
    });
  }

  /**
   * Opens the geofencing configuration dialog — required before an embeddings search can run.
   */
  openGeofencingDialog(): void {
    this.geofencingFilterService.openDialog({});
  }
}
